// src/services/siiService.js
import { apiClient } from './api';
import { store } from '@/redux/store';

/**
 * Servicio para interactuar con los endpoints de SII
 */
export const siiService = {
  /**
   * Obtener certificados disponibles en el gestor del sistema
   * @returns {Promise<Array>} - Promesa con la lista de certificados disponibles
   */
  async getCertificadosDisponibles() {
    return apiClient.get('/sii/certificates');
  },
  
  /**
  * Obtener certificados disponibles para la empresa activa
  * @returns {Promise<Array>} - Promesa con la lista de certificados
  */
  async getCertificadosEmpresa() {
    const { empresaActiva } = store.getState().empresa;
      
    if (!empresaActiva) {
      return Promise.reject(new Error('No hay una empresa seleccionada'));
    }
      
    return apiClient.get(`/empresas/certificados/empresa/${empresaActiva.rut}?activos_only=true`);
  },

  /**
   * Autenticar usando el gestor de certificados del backend
   * @param {string} certificateId - ID del certificado a utilizar
   * @returns {Promise<string>} - Promesa con el tokenSII
   */
  async authenticateWithCertificateManager(certificateId) {
    // Asegurar que certificateId sea un string
    certificateId = String(certificateId);
    
    const response = await apiClient.post('/sii/authenticate', { certificateId });
    return response.token;
  },

  /**
   * Autenticar usando un certificado de la empresa
   * @param {string} certificadoId - ID del certificado de empresa a utilizar
   * @returns {Promise<string>} - Promesa con el tokenSII
   */
  async authenticateWithEmpresaCertificado(certificadoId) {
    // Asegurar que certificadoId sea un string
    certificadoId = String(certificadoId);
    
    const response = await apiClient.post('/sii/authenticate/empresa', { certificateId: certificadoId });
    return response.token;
  },
  
  /**
   * Obtener una semilla del SII
   * @returns {Promise<string>} - Promesa con la semilla
   */
  async getSeed() {
    const response = await apiClient.get('/sii/seed');
    return response.seed;
  },

  /**
   * Firmar una semilla con un certificado digital
   * @param {File} certificate - Archivo del certificado PFX
   * @param {string} seed - Semilla a firmar
   * @param {string} password - Contraseña del certificado
   * @returns {Promise<Object>} - Promesa con la semilla firmada y el SOAP request
   */
  async signSeed(certificate, seed, password) {
    const formData = new FormData();
    formData.append('certificate', certificate);
    formData.append('seed', seed);
    formData.append('password', password);

    return apiClient.uploadFile('/sii/sign', formData);
  },

  /**
   * Obtener un tokenSII con la semilla firmada
   * @param {string} signedSeed - Semilla firmada en formato XML
   * @returns {Promise<string>} - Promesa con el tokenSII
   */
  async getToken(signedSeed) {
    const response = await apiClient.post('/sii/token', { signedSeed });
    return response.token;
  },

  /**
   * Flujo completo de autenticación con certificado
   * @param {File} certificate - Archivo del certificado PFX
   * @param {string} password - Contraseña del certificado
   * @returns {Promise<string>} - Promesa con el tokenSII
   */
  async authenticate(certificate, password) {
    // 1. Obtener semilla
    const seed = await this.getSeed();
    
    // 2. Firmar semilla
    const { signedXml } = await this.signSeed(certificate, seed, password);
    
    // 3. Obtener tokenSII
    const tokenSII = await this.getToken(signedXml);
    
    return tokenSII;
  },

  /**
   * Consultar RCV
   * @param {Object} params - Parámetros para la consulta
   * @returns {Promise<Object>} - Promesa con los resultados
   */
  async consultarRcv(params) {
    if (!params.conversationId) {
      throw new Error('TokenSII de autenticación requerido');
    }

    const { empresaActiva } = store.getState().empresa;
    if (!empresaActiva) {
      throw new Error('No hay una empresa seleccionada');
    }

    const rutEmisor = params.rutEmisor || params.rut_empresa || empresaActiva.rut;
    const dvEmisor = params.dvEmisor || params.dv_empresa || empresaActiva.dv;
    const tipoOperacion = params.tipoOperacion || params.tipo_operacion || 'COMPRA';

    const requestData = {
      rut_empresa: rutEmisor,
      dv_empresa: dvEmisor,
      periodo: params.periodo,
      tipo_operacion: tipoOperacion,
      estado_contab: params.estadoContab || params.estado_contab || 'REGISTRO',
      recaptcha_token: params.recaptchaToken || params.recaptcha_token,
      conversationId: params.conversationId,
    };

    if (tipoOperacion === 'VENTA') {
      return apiClient.post('/rcv/ventas/consultar', requestData);
    }

    return apiClient.post('/rcv/consultar', requestData);
  }
};